"use client";

import { useEffect, useRef, useState, type RefObject, type CSSProperties } from "react";

interface UseAppearOnScrollOptions {
    from?: "fromLeft" | "fromRight" | "fromBottom" | "fromTop" | "fadeIn";
    duration?: number;
    delay?: number;
    offset?: number;
    once?: boolean;
}

export default function useAppearOnScroll<T extends HTMLElement>({
                                                                     from = "fromBottom",
                                                                     duration = 1000,
                                                                     delay = 0,
                                                                     offset = 0,
                                                                     once = true,
                                                                 }: UseAppearOnScrollOptions): {
    ref: RefObject<T | null>;
    className: string;
    style: CSSProperties;
} {
    const ref = useRef<T | null>(null);
    const [isVisible, setIsVisible] = useState(false);
    const didAnimateRef = useRef(false);

    const prefersReducedMotion =
        typeof window !== "undefined" &&
        window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

    useEffect(() => {
        const node = ref.current;
        if (!node) return;

        const vh = typeof window !== "undefined"
            ? (window.visualViewport?.height ?? window.innerHeight)
            : 0;
        const topMargin = -vh / 9 + offset;

        const io = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    if (once && didAnimateRef.current) return;
                    didAnimateRef.current = true;
                    requestAnimationFrame(() => setIsVisible(true));
                    if (once) io.disconnect();
                } else if (!once) {
                    requestAnimationFrame(() => setIsVisible(false));
                }
            },
            {
                threshold: 0.1,
                rootMargin: `${topMargin}px 0px 0px 0px`,
            }
        );

        io.observe(node);
        return () => io.disconnect();
    }, [offset, once]);
    const style: CSSProperties = prefersReducedMotion
        ? {}
        : {
            animationDuration: `${duration}ms`,
            animationDelay: `${delay}ms`,
            willChange: isVisible ? "auto" : "transform, opacity",
        };

    const className = prefersReducedMotion ? ""
        : isVisible
            ? `animate ${from}`
            : `hiddenBefore`;

    return { ref, className, style };
}
